<template>
    <div class="columns" v-if="initDone">
        <div class="column" v-if="pricingDefinitions.length > 0">
            <div class="box" v-for="(pricingDefinition, index) in pricingDefinitions" :key="index">
                <h5 class="is-size-5 mb-2 has-border">
                    {{ translate("pricing.registration_stage") }} #{{ pricingDefinition.registrationStageNumber }}
                </h5>

                <div style="line-height: 1.7" class="mb-4">
                    <div>
                        <span class="tag is-success" v-if="pricingDefinition.active">
                            {{ translate("pricing.active") }}
                        </span>
                    </div>

                    <div>
                        <span class="is-description">{{ translate("pricing.start") }}:</span>
                        <span class="mr-2">
                            {{ convertToTimezone(pricingDefinition.startDate, eventTimezone, "yyyy-MM-dd HH:mm") }}
                        </span>
                    </div>

                    <div class="mb-2">
                        <span class="is-description">{{ translate("pricing.end") }}:</span>
                        <span class="mr-2">
                            {{ convertToTimezone(pricingDefinition.endDate, eventTimezone, "yyyy-MM-dd HH:mm") }}
                        </span>
                    </div>

                    <Attribute v-for="(attribute, attrIndex) in pricingDefinition.attributes" :attribute="attribute" :key="attrIndex" />
                </div>

                <div class="buttons are-small">
                    <button class="button" @click="editPricingDefinition(pricingDefinition)">
                        <span class="icon"><i class="fas fa-edit"></i></span><span>{{ translate("common.edit") }}</span>
                    </button>
                    <button class="button is-danger" @click="removePricingDefinition(pricingDefinition.id)">
                        <span class="icon"><i class="fas fa-trash-alt"></i></span>
                        <span>{{ translate("common.delete") }}</span>
                    </button>
                </div>
            </div>
        </div>
        <div v-else class="column is-6 is-offset-3">
            <Notification class="is-info has-text-centered">
                <span class="icon"><i class="fas fa-info-circle"></i></span>
                <span>{{ translate("pricing.pricing_not_defined") }}</span>
            </Notification>
        </div>
    </div>
    <div v-else class="columns is-mobile is-centered">
        <LoadingSpinner2 class="has-added-margin" />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted } from "vue";
    import { translate } from "../Common/translations";
    import Attribute from "./Attributes/Attribute.vue";
    import Notification from "../Common/Notification.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { eventBus, OPEN_EDIT_PRICING_MODAL } from "./eventBus";
    import { convertToTimezone } from "../Common/dateTimeUtils";
    import pricingApi from "../Api/pricing";
    import { handleError } from "../Common/errorHandler";
    import { usePricingSetupStore } from "./pricingSetupApp";

    const store = usePricingSetupStore();

    const initDone = ref(false);
    const pricingDefinitions = computed(() => store.pricingDefinitions);
    const eventId = computed(() => store.eventId);
    const eventTimezone = computed(() => store.eventTimezone);

    const fetchAllDefinitions = () => {
        return store.refreshPricingDefinitions();
    };

    const removePricingDefinition = pricingId => {
        if (!confirm(translate("common.are_you_sure"))) {
            return;
        }

        return pricingApi
            .removePricing(eventId.value, pricingId)
            .then(() => {
                return store.refreshPricingDefinitions();
            })
            .catch(handleError);
    };

    const editPricingDefinition = pricingDefinition => {
        eventBus.$emit(OPEN_EDIT_PRICING_MODAL, pricingDefinition);
    };

    onMounted(() => {
        initDone.value = false;
        fetchAllDefinitions()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    });
</script>

<style scoped></style>
